/**
 * Anexos (docs/adr/0011-avisos-anexos-e-formulario-publico.md): limite de tamanho e tipos aceitos.
 * Vale para qualquer módulo que guarde arquivos; a API confere tudo de novo, as telas só ajudam.
 */

/** 20 MB por arquivo. */
export const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

type Signature = 'pdf' | 'png' | 'jpeg' | 'gif' | 'webp' | 'zip' | 'ole' | 'text';

/** Tipos aceitos, pela extensão do nome. O tipo gravado é sempre o daqui, nunca o que o navegador diz. */
const TYPES: Record<string, { contentType: string; signature: Signature }> = {
  pdf: { contentType: 'application/pdf', signature: 'pdf' },
  png: { contentType: 'image/png', signature: 'png' },
  jpg: { contentType: 'image/jpeg', signature: 'jpeg' },
  jpeg: { contentType: 'image/jpeg', signature: 'jpeg' },
  gif: { contentType: 'image/gif', signature: 'gif' },
  webp: { contentType: 'image/webp', signature: 'webp' },
  xlsx: {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    signature: 'zip',
  },
  xls: { contentType: 'application/vnd.ms-excel', signature: 'ole' },
  ods: { contentType: 'application/vnd.oasis.opendocument.spreadsheet', signature: 'zip' },
  docx: {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    signature: 'zip',
  },
  doc: { contentType: 'application/msword', signature: 'ole' },
  odt: { contentType: 'application/vnd.oasis.opendocument.text', signature: 'zip' },
  csv: { contentType: 'text/csv', signature: 'text' },
  txt: { contentType: 'text/plain', signature: 'text' },
};

/** Extensões aceitas, para o seletor de arquivos das telas (".pdf,.png,..."). */
export const ATTACHMENT_ACCEPT = Object.keys(TYPES)
  .map((ext) => `.${ext}`)
  .join(',');

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot < 0 ? '' : fileName.slice(dot + 1).toLowerCase();
}

/** O tipo do arquivo pelo nome, ou null se não for aceito. */
export function attachmentContentType(fileName: string): string | null {
  return TYPES[extensionOf(fileName)]?.contentType ?? null;
}

const startsWith = (head: Uint8Array, bytes: number[], offset = 0) =>
  bytes.every((b, i) => head[offset + i] === b);

/**
 * O começo do arquivo combina com a extensão? Evita, por exemplo, um programa renomeado para ".pdf".
 * `head` são os primeiros bytes (16 bastam).
 */
export function attachmentMatchesContent(fileName: string, head: Uint8Array): boolean {
  const type = TYPES[extensionOf(fileName)];
  if (!type) return false;
  switch (type.signature) {
    case 'pdf':
      return startsWith(head, [0x25, 0x50, 0x44, 0x46]); // %PDF
    case 'png':
      return startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case 'jpeg':
      return startsWith(head, [0xff, 0xd8, 0xff]);
    case 'gif':
      return startsWith(head, [0x47, 0x49, 0x46, 0x38]); // GIF8
    case 'webp':
      return (
        startsWith(head, [0x52, 0x49, 0x46, 0x46]) && startsWith(head, [0x57, 0x45, 0x42, 0x50], 8)
      );
    case 'zip':
      return startsWith(head, [0x50, 0x4b, 0x03, 0x04]); // PK
    case 'ole':
      return startsWith(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
    case 'text':
      // Texto não tem assinatura: só recusa o que claramente é binário (byte zero).
      return !head.includes(0);
  }
}

/** Nome seguro para gravar e mostrar: sem pastas nem caracteres de controle, até 255 letras. */
export function cleanFileName(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex
  const clean = base.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (clean.length <= 255) return clean;
  const ext = extensionOf(clean);
  return `${clean.slice(0, 254 - ext.length)}.${ext}`;
}
